'use client';

import { Globe, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@kit/ui/card';
import { Label } from '@kit/ui/label';
import { cn } from '@kit/ui/utils';

import { getCountryName } from '../../../lib/countries';
import type { AddressValue } from './types';

interface AddressInputViewProps {
  value: AddressValue;
  className?: string;
  label?: string;
  hint?: string;
}

/**
 * Read-only presentation of an address. Used by `<AddressInput mode="view" />`
 * to consolidate the hub's display block with the same surface that
 * renders the editor — so view and edit can never drift out of sync.
 *
 * Renders a semantic `<address>` block. Hides empty optional fields
 * gracefully (apartment, stateProvince).
 */
export function AddressInputView({
  value,
  className,
  label,
  hint,
}: AddressInputViewProps) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('dancer:addressInput.label');
  const {
    streetAddress,
    apartment,
    city,
    stateProvince,
    postalCode,
    countryCode,
  } = value;
  const countryName = countryCode ? getCountryName(countryCode) : '';

  return (
    <Card className={cn('p-3 sm:p-4', className)}>
      <CardContent className="space-y-3 p-0 sm:space-y-4">
        <div className="mb-2">
          <div className="mb-1 flex items-center gap-2">
            <MapPin className="text-muted-foreground h-4 w-4" />
            <Label className="text-sm font-medium">{resolvedLabel}</Label>
          </div>
          {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
        </div>

        <address className="text-foreground space-y-1 text-sm not-italic">
          {streetAddress && <p>{streetAddress}</p>}
          {apartment && <p className="text-muted-foreground">{apartment}</p>}
          {(postalCode || city) && (
            <p>{[postalCode, city].filter(Boolean).join(' ')}</p>
          )}
          {stateProvince && (
            <p className="text-muted-foreground">{stateProvince}</p>
          )}
          {countryName && (
            <p className="text-muted-foreground flex items-center gap-1">
              <Globe className="h-3 w-3" />
              <span>{countryName}</span>
            </p>
          )}
        </address>
      </CardContent>
    </Card>
  );
}
